import React, { useState } from 'react';
import {
  BuildingOfficeIcon,
  HomeIcon,
  BeakerIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const cases = [
  {
    icon: BuildingOfficeIcon,
    title: 'ЖК "Нурлы Тау"',
    description: 'Поставка труб для системы водоснабжения и канализации жилого комплекса',
    details: '2500 метров труб различного диаметра',
    year: '2023'
  },
  {
    icon: HomeIcon,
    title: 'Коттеджный поселок "Алатау"',
    description: 'Комплексное обеспечение трубами для инженерных систем',
    details: '1800 метров труб, фитинги',
    year: '2023'
  },
  {
    icon: BeakerIcon,
    title: 'Завод "КазХимПром"',
    description: 'Модернизация системы технического водоснабжения',
    details: '3200 метров промышленных труб',
    year: '2022'
  }
];

const reviews = [
  {
    id: 1,
    image: '/images/reviews/1.jpg',
    alt: 'Отзыв клиента 1'
  },
  {
    id: 2,
    image: '/images/reviews/2.jpg',
    alt: 'Отзыв клиента 2'
  },
  {
    id: 3,
    image: '/images/reviews/3.jpg',
    alt: 'Отзыв клиента 3'
  },
  {
    id: 4,
    image: '/images/reviews/4.jpg',
    alt: 'Отзыв клиента 4'
  },
  {
    id: 5,
    image: '/images/reviews/5.jpg',
    alt: 'Отзыв клиента 5'
  },
  {
    id: 6,
    image: '/images/reviews/6.jpg',
    alt: 'Отзыв клиента 6'
  },
  {
    id: 7,
    image: '/images/reviews/7.jpg',
    alt: 'Отзыв клиента 7'
  }
];

const CasesSection = () => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [selectedReview, setSelectedReview] = useState<(typeof reviews)[0] | null>(null);

  const nextReview = () => {
    setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReviewIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const getReviewsPerView = () => {
    if (typeof window === 'undefined') return 4;
    if (window.innerWidth >= 1024) return 4;
    if (window.innerWidth >= 768) return 2;
    return 1;
  };

  const getVisibleReviews = () => {
    const reviewsPerView = getReviewsPerView();
    const visible = [];

    for (let i = 0; i < reviewsPerView; i++) {
      visible.push(reviews[(currentReviewIndex + i) % reviews.length]);
    }

    return visible;
  };

  return (
    <section className="py-16 bg-gray-50 dark:bg-secondary-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Реализованные проекты
          </h2>
          <p className="text-xl text-gray-600 dark:text-secondary-300">
            Более 500 успешных проектов по всему Казахстану
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cases.map((project, index) => (
            <div
              key={index}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <project.icon className="h-12 w-12 text-primary-600" />
                <span className="text-sm text-gray-500 dark:text-secondary-400">{project.year}</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                {project.title}
              </h3>
              <p className="text-gray-600 dark:text-secondary-300 mb-4">{project.description}</p>
              <p className="text-sm text-primary-600 font-medium">{project.details}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Отзывы наших клиентов
            </h3>
            <p className="text-lg text-gray-600 dark:text-secondary-300">
              Доверие клиентов - наша главная награда
            </p>
          </div>

          <div className="relative">
            <button
              onClick={prevReview}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-secondary-800/90 rounded-full p-2 shadow-md hover:scale-110 transition hidden md:flex"
              aria-label="Предыдущие отзывы"
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-700 dark:text-white" />
            </button>

            <button
              onClick={nextReview}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-secondary-800/90 rounded-full p-2 shadow-md hover:scale-110 transition hidden md:flex"
              aria-label="Следующие отзывы"
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-700 dark:text-white" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:px-12">
              {getVisibleReviews().map((review) => (
                <div
                  key={review.id}
                  onClick={() => setSelectedReview(review)}
                  className="cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all transform hover:scale-105 bg-white dark:bg-secondary-800"
                >
                  <img
                    src={review.image}
                    alt={review.alt}
                    className="w-full h-80 object-cover hover:opacity-90 transition-opacity"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-2 mt-6">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentReviewIndex(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentReviewIndex
                      ? 'bg-primary-600 w-6'
                      : 'bg-gray-300 dark:bg-secondary-600'
                  }`}
                  aria-label={`Перейти к отзыву ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-lg max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-secondary-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {selectedReview.alt}
              </h3>
              <button
                onClick={() => setSelectedReview(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-secondary-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center">
              <img
                src={selectedReview.image}
                alt={selectedReview.alt}
                className="max-w-full max-h-[calc(90vh-120px)] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CasesSection;